import { AxiosError, AxiosRequestConfig } from 'axios';
import { apiGet, apiPost, apiPut } from 'services/api';
import {
  ICreateCatalogProductBody,
  ICreatedCatalogProduct,
  ILastQuote,
  IPlanMultiplierResponse,
  IProcurementItem,
  IQuoteFullDetailsResponse,
  IQuoteItemDetailsForQuoteManagement,
  IQuoteItemOriginalAttributes,
  IQuoteItemReason,
  ISendQuoteBody
} from 'types/quoteEditor.types';

const { REACT_APP_CATALOG, REACT_APP_ORGANIZATION } = process.env;

/**
 * Trae el detalle enriquecido de una cotización (endpoint full-details).
 * Se usa solo como fuente de fallback de `specifications` para los ítems genéricos,
 * ya que el endpoint `items` no resuelve esas specs.
 */
const getQuoteFullDetails = async (quoteId: number) => {
  return apiGet<AxiosRequestConfig, IQuoteFullDetailsResponse>(
    `${REACT_APP_CATALOG}quote-actions/full-details/${quoteId}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/** Indica si un objeto de specs está vacío o ausente. */
const hasNoSpecifications = (specifications?: Record<string, string> | null): boolean =>
  !specifications || Object.keys(specifications).length === 0;

/**
 * Trae el listado detallado de ítems de una cotización para el editor,
 * incluyendo specs del cliente, flags de producto y sugerencias previas.
 * Para los ítems genéricos completa las `specifications` faltantes con las del
 * endpoint full-details (que sí las resuelve con fallback a las pills del producto).
 */
const getQuoteEditorItems = async (quoteId: number): Promise<IQuoteItemDetailsForQuoteManagement[]> => {
  const [items, fullDetails] = await Promise.all([
    apiGet<AxiosRequestConfig, IQuoteItemDetailsForQuoteManagement[]>(
      `${REACT_APP_CATALOG}quote-actions/items/${quoteId}`
    ),
    getQuoteFullDetails(quoteId).catch(() => null)
  ]);

  if (!fullDetails?.items?.length) return items;

  const specsByQuoteItemId = new Map<number, Record<string, string>>();
  fullDetails.items.forEach(detail => {
    if (!hasNoSpecifications(detail?.specifications)) {
      specsByQuoteItemId.set(detail.quoteItemId, detail.specifications!);
    }
  });

  return items.map(item => {
    if (!item.isGenericProduct || !hasNoSpecifications(item.specifications)) return item;
    const fallbackSpecs = specsByQuoteItemId.get(item.quoteItemId);
    return fallbackSpecs ? { ...item, specifications: fallbackSpecs } : item;
  });
};

/**
 * Trae las últimas 3 cotizaciones históricas de un SKU en un país específico (últimos 365 días).
 */
const getLastQuotesByProduct = async (sku: string, countryCode: string) => {
  return apiGet<AxiosRequestConfig, ILastQuote[]>(
    `${REACT_APP_CATALOG}quote-actions/sku-price-history?sku=${encodeURIComponent(
      sku
    )}&countryCode=${encodeURIComponent(countryCode)}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Trae los atributos originales de un ítem de cotización (proveedor, MPN, costo, ETA).
 */
const getQuoteItemOriginalAttributes = async (quoteItemId: number) => {
  return apiGet<AxiosRequestConfig, IQuoteItemOriginalAttributes>(
    `${REACT_APP_CATALOG}quote-item-actions/original-attributes/${quoteItemId}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Actualiza un ítem de cotización con los datos de procurement (precio, proveedor, ETA, etc.).
 * Puede incluir ítems sugeridos como alternativas al producto principal.
 */
const updateQuoteItem = async (body: IProcurementItem) => {
  return apiPut<AxiosRequestConfig, IProcurementItem, void>(
    `${REACT_APP_CATALOG}quote-item-actions/update-quote-item`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Envía la cotización completa para ser aprobada por el usuario.
 * Requiere que todos los ítems estén marcados como cotizados.
 */
const sendQuoteFromProcurement = async (quoteId: number, body: ISendQuoteBody) => {
  return apiPost<AxiosRequestConfig, ISendQuoteBody, void>(
    `${REACT_APP_CATALOG}quote-actions/send-quote-from-procurement/${quoteId}`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Trae los precios activos en tienda de un producto para un país específico.
 */
const getProductExchangePrice = async (productId: number, countryCode: string) => {
  return apiGet<AxiosRequestConfig, unknown>(
    `${REACT_APP_CATALOG}exchange-price/product/${productId}/country/${countryCode}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Trae el multiplicador de precio según el plan y preferencia comercial de una organización.
 */
const getPlanMultiplier = async (organizationId: number) => {
  return apiGet<AxiosRequestConfig, IPlanMultiplierResponse>(
    `${REACT_APP_ORGANIZATION}/platform-subscription/plan-multiplier/${organizationId}`
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/**
 * Crea un nuevo producto de catálogo asociado a un ítem de cotización,
 * incluyendo especificaciones técnicas según la categoría del producto.
 */
const createCatalogProductForQuoteItem = async (body: ICreateCatalogProductBody) => {
  return apiPost<AxiosRequestConfig, ICreateCatalogProductBody, ICreatedCatalogProduct>(
    `${REACT_APP_CATALOG}quote-item-backoffice/create-catalog-product-for-quote-item`,
    body
  )
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

/** Trae el listado de razones disponibles para sugerir una herramienta alternativa. */
const getQuoteItemReasons = async () => {
  return apiGet<AxiosRequestConfig, IQuoteItemReason[]>(`${REACT_APP_CATALOG}quote-item-reason?isForProcurement=true`)
    .then(response => Promise.resolve(response))
    .catch((error: AxiosError) => Promise.reject(error));
};

export const QuoteEditorService = {
  getQuoteEditorItems,
  getQuoteFullDetails,
  getLastQuotesByProduct,
  getQuoteItemOriginalAttributes,
  updateQuoteItem,
  sendQuoteFromProcurement,
  getProductExchangePrice,
  getPlanMultiplier,
  createCatalogProductForQuoteItem,
  getQuoteItemReasons
};
